import React, { useCallback, useState } from 'react';
import { createStyles } from '@/contexts/theme-context';
import { Dropdown } from '@usepepper/ui/atoms/dropdown';
import { FulfillmentActionSheetFulfillmentDaysView } from './fulfillment-action-sheet-fulfillment-days-view';
import { FulfillmentActionSheetOptionsView } from './fulfillment-action-sheet-options-view';
import {
  FulfillmentActionSheetRoute,
  useFulfillmentPicker,
} from './use-fulfillment-picker';
import { JumpToFn } from '@/components/combined-action-sheet';
import { LayoutChangeEvent, StyleProp, View, ViewStyle } from 'react-native';
import { NewOrder_OrderFieldsFragment } from '@/types';
import { TrackingProperties } from '@/utils/analytics/base-reporter';

const POPOVER_WIDTH = 400;

interface Props {
  activeRoute: FulfillmentActionSheetRoute | null;
  draftOrder: NewOrder_OrderFieldsFragment | null;
  jumpTo: JumpToFn<FulfillmentActionSheetRoute>;
  onClose: () => void;
  picker: ReturnType<typeof useFulfillmentPicker>;
  style?: StyleProp<ViewStyle>;
  trackingProperties: TrackingProperties | null;
  trigger: React.ReactNode;
}

export const FulfillmentPickerPopover: React.FC<Props> = ({
  activeRoute,
  draftOrder,
  jumpTo,
  onClose,
  picker,
  style,
  trackingProperties,
  trigger,
}) => {
  const [styles] = useStyles();
  const [triggerWidth, setTriggerWidth] = useState<number | undefined>();
  const handleTriggerLayout = useCallback((event: LayoutChangeEvent) => {
    setTriggerWidth(event.nativeEvent.layout.width);
  }, []);

  const {
    clearTimeslot,
    listData,
    saveFulfillment,
    saveFulfillmentForToday,
    savingFulfillment,
    selectedFulfillmentDate,
    selectedFulfillmentOption,
    selectedTimeslot,
    setSelectedFulfillmentDate,
    setSelectedFulfillmentOption,
    setSelectedTimeslot,
  } = picker;

  const renderContent = () => {
    if (activeRoute === FulfillmentActionSheetRoute.Base) {
      return (
        <FulfillmentActionSheetOptionsView
          isLoading={false}
          listData={listData}
          onSelect={(option) => {
            const { fulfillmentOption, nextRouteKey } = option.metadata!;
            if (nextRouteKey) {
              setSelectedFulfillmentOption(fulfillmentOption);
              jumpTo(nextRouteKey);
              return;
            }
            saveFulfillmentForToday(fulfillmentOption, onClose);
          }}
          savingFulfillment={savingFulfillment}
        />
      );
    }

    if (
      activeRoute === FulfillmentActionSheetRoute.DaysView &&
      selectedFulfillmentOption
    ) {
      return (
        <FulfillmentActionSheetFulfillmentDaysView
          clearTimeslot={clearTimeslot}
          draftOrder={draftOrder}
          fulfillmentOption={selectedFulfillmentOption}
          initialFulfillmentDate={selectedFulfillmentDate}
          isSaving={Boolean(savingFulfillment)}
          onDayChange={setSelectedFulfillmentDate}
          onSave={(dateMoment, timeslot, resolvedFulfillmentOption) => {
            saveFulfillment(
              {
                date: dateMoment.toISOString(),
                fulfillmentSettingsUUID:
                  resolvedFulfillmentOption.fulfillment_settings_id,
                timeslot,
                type: resolvedFulfillmentOption.fulfillment_name,
              },
              onClose,
            );
          }}
          onSelectTimeslot={setSelectedTimeslot}
          selectedTimeslot={selectedTimeslot}
          trackingProperties={trackingProperties}
        />
      );
    }

    return null;
  };

  const isTypeList = activeRoute === FulfillmentActionSheetRoute.Base;
  const content = renderContent();

  if (!content) {
    return (
      <View onLayout={handleTriggerLayout} style={style}>
        {trigger}
      </View>
    );
  }

  return (
    <View onLayout={handleTriggerLayout} style={style}>
      <Dropdown
        containerStyle={[
          styles.dropdownContent,
          isTypeList ? styles.dropdownContentTypeList : null,
          isTypeList && triggerWidth ? { width: triggerWidth } : null,
        ]}
        isVisible
        onClose={onClose}
        position="bottom-start"
        trigger={trigger}
      >
        {content}
      </Dropdown>
    </View>
  );
};

const useStyles = createStyles((theme) => ({
  dropdownContent: {
    paddingHorizontal: theme.sk.spacing.spaceSmall,
    paddingVertical: theme.sk.spacing.spaceMedium,
    width: POPOVER_WIDTH,
  },
  dropdownContentTypeList: {
    paddingHorizontal: 0,
    paddingVertical: theme.sk.spacing.spaceSmall,
  },
}));
